import {
	Box,
	Dialog,
	For,
	HStack,
	Image,
	Portal,
	VStack,
} from '@chakra-ui/react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import type {
	CustomDialogBackdropProps,
	CustomDialogContentProps,
	CustomDialogPositionerProps,
	CustomDialogTitleProps,
} from '@/components/Atoms/FaceliftDialog/FaceliftDialog.types';
import {
	handleCrop,
	handleCropMouseDown,
	handleMouseMove,
	handleResizeMouseDown,
} from './helperFunctions';

interface CropPhotoDialogProps {
	isOpen: boolean;
	onClose: () => void;
	imageUrl: string;
	onCropComplete: (croppedImageUrl: string) => void;
}

const CustomDialog = {
	Backdrop: (props: CustomDialogBackdropProps) => (
		<Dialog.Backdrop {...props} />
	),
	Positioner: (props: CustomDialogPositionerProps) => (
		<Dialog.Positioner {...props} />
	),
	Content: (props: CustomDialogContentProps) => <Dialog.Content {...props} />,
	Title: (props: CustomDialogTitleProps) => <Dialog.Title {...props} />,
};

const CropPhotoDialog: React.FC<CropPhotoDialogProps> = ({
	isOpen,
	onClose,
	imageUrl,
	onCropComplete,
}) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const [cropArea, setCropArea] = useState({
		x: 75,
		y: 75,
		width: 250,
		height: 250,
	});

	const [isDragging, setIsDragging] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [resizeHandle, setResizeHandle] = useState<string>('');
	const [initialCropArea, setInitialCropArea] = useState(cropArea);

	const handleMouseMoveWrapper = useCallback(
		(e: MouseEvent) => {
			handleMouseMove(
				e,
				isDragging,
				isResizing,
				containerRef,
				dragStart,
				cropArea,
				resizeHandle,
				initialCropArea,
				setCropArea,
			);
		},
		[
			isDragging,
			isResizing,
			dragStart,
			cropArea,
			resizeHandle,
			initialCropArea,
		],
	);

	const handleMouseUp = useCallback(() => {
		setIsDragging(false);
		setIsResizing(false);
		setResizeHandle('');
	}, []);

	useEffect(() => {
		if (isDragging || isResizing) {
			document.addEventListener('mousemove', handleMouseMoveWrapper);
			document.addEventListener('mouseup', handleMouseUp);
			return () => {
				document.removeEventListener('mousemove', handleMouseMoveWrapper);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [isDragging, isResizing, handleMouseMoveWrapper, handleMouseUp]);

	const handleCropWrapper = () => {
		handleCrop(canvasRef, imageRef, cropArea, onCropComplete, onClose);
	};

	const handleCancel = () => {
		onClose();
	};

	return (
		<Dialog.Root
			open={isOpen}
			onOpenChange={({ open }: { open: boolean }) => !open && onClose()}
		>
			<Portal>
				<CustomDialog.Backdrop bg="blackAlpha.600" />
				<CustomDialog.Positioner>
					<CustomDialog.Content width="400px" maxH="500px" bg="brand.pageBackground">
						<Dialog.Header bg="brand.500" w="100%" justifyContent="center">
							<CustomDialog.Title>
								<Box fontSize="20px" fontWeight="bold" color="brand.100">
									Crop Photo
								</Box>
							</CustomDialog.Title>
						</Dialog.Header>

						<Dialog.Body px={1} py={4}>
							<VStack gap={4}>
								<Box
									ref={containerRef}
									position="relative"
									w="350px"
									h="350px"
									bg="gray.100"
									border="2px solid"
									borderColor="gray.300"
									overflow="hidden"
									mx="auto"
								>
									<Image
										ref={imageRef}
										src={imageUrl}
										alt="Crop preview"
										w="100%"
										h="100%"
										objectFit="cover"
										transform={`scale(1)`}
										transition="transform 0.1s"
										draggable={false}
										userSelect="none"
									/>

									<Box
										position="absolute"
										top="0"
										left="0"
										right="0"
										bottom="0"
										pointerEvents="none"
									>
										<Box
											position="absolute"
											top="0"
											left="0"
											right="0"
											bottom="0"
											bg="overlay.dark"
										/>

										<Box
											position="absolute"
											left={`${cropArea.x}px`}
											top={`${cropArea.y}px`}
											width={`${cropArea.width}px`}
											height={`${cropArea.height}px`}
											bg="transparent"
											boxShadow="0 0 0 9999px token(colors.overlay.dark)"
										/>
									</Box>

									<Box
										position="absolute"
										left={`${cropArea.x}px`}
										top={`${cropArea.y}px`}
										width={`${cropArea.width}px`}
										height={`${cropArea.height}px`}
										border="2px solid white"
										cursor={isDragging ? 'grabbing' : 'move'}
										onMouseDown={(e) =>
											handleCropMouseDown(
												e,
												containerRef as React.RefObject<HTMLDivElement>,
												setIsDragging,
												setDragStart,
												cropArea,
											)
										}
										pointerEvents="auto"
									>
										<For each={['nw', 'ne', 'sw', 'se']}>
											{(handle, index) => (
												<Box
													key={index}
													position="absolute"
													w="8px"
													h="8px"
													bg="white"
													border="1px solid"
													borderColor="gray.400"
													cursor={
														handle.includes('n')
															? handle.includes('w')
																? 'nw-resize'
																: 'ne-resize'
															: handle.includes('w')
																? 'sw-resize'
																: 'se-resize'
													}
													onMouseDown={(e) =>
														handleResizeMouseDown(
															e,
															handle,
															setIsResizing,
															setResizeHandle,
															setInitialCropArea,
															setDragStart,
															cropArea,
														)
													}
													{...(handle.includes('n')
														? { top: '-4px' }
														: { bottom: '-4px' })}
													{...(handle.includes('w')
														? { left: '-4px' }
														: { right: '-4px' })}
												/>
											)}
										</For>

										<For each={['n', 's', 'w', 'e']}>
											{(handle, index) => (
												<Box
													key={index}
													position="absolute"
													bg="white"
													border="1px solid"
													borderColor="gray.400"
													cursor={
														handle === 'n' || handle === 's'
															? 'ns-resize'
															: 'ew-resize'
													}
													onMouseDown={(e) =>
														handleResizeMouseDown(
															e,
															handle,
															setIsResizing,
															setResizeHandle,
															setInitialCropArea,
															setDragStart,
															cropArea,
														)
													}
													{...(handle === 'n'
														? {
															top: '-4px',
															left: '50%',
															transform: 'translateX(-50%)',
															w: '8px',
															h: '8px',
														}
														: handle === 's'
															? {
																bottom: '-4px',
																left: '50%',
																transform: 'translateX(-50%)',
																w: '8px',
																h: '8px',
															}
															: handle === 'w'
																? {
																	left: '-4px',
																	top: '50%',
																	transform: 'translateY(-50%)',
																	w: '8px',
																	h: '8px',
																}
																: {
																	right: '-4px',
																	top: '50%',
																	transform: 'translateY(-50%)',
																	w: '8px',
																	h: '8px',
																})}
												/>
											)}
										</For>
									</Box>
								</Box>
							</VStack>
						</Dialog.Body>

						<Dialog.Footer>
							<HStack gap={3} w={'100%'} justifyContent="center">
								<FaceliftButton
									width={'170px'}
									variant="outline"
									onClick={handleCropWrapper}
									borderRadius="0px"
								>
									Select
								</FaceliftButton>
								<FaceliftButton
									width={'170px'}
									bg={{ _dark: 'brand.cardBackground', _light: 'transparent' }}
									color={{ _dark: 'brand.white', _light: 'brand.950' }}
									variant="outline"
									onClick={handleCancel}
									borderRadius="0px"
								>
									Cancel
								</FaceliftButton>
							</HStack>
						</Dialog.Footer>

						<canvas ref={canvasRef} style={{ display: 'none' }} />
					</CustomDialog.Content>
				</CustomDialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default CropPhotoDialog;
